/** Workbook spec for the styled Privilege Report XLSX.
 *
 *  Pure, dependency-free: turns the resolver payload into a structured spec —
 *  sheets, rows, per-cell style tokens, widths — that privilegeReportXlsx.js
 *  renders with ExcelJS. The workbook is the stakeholder view of the JSON
 *  mirror, two tabs: "All Users" (staff + patients merged, one row per user ×
 *  privilege, Risk first, Method instead of Status) and "GroupPrivileges"
 *  (Risk first). Rows are grouped into blocks (per user / per group) and
 *  values repeated from the previous row are left blank for readability —
 *  the stakeholder-requested de-duplication style. Risk cells are never
 *  blanked. The JSON export keeps the full normalized collections.
 */

import { buildAllUsersRows, buildReportSheets, SHEET_HEADERS } from "@/utils/privilegeReportRows";

// Palette lifted from the approved preview (ARGB, Excel-style).
export const COLORS = {
  navy: "FF1F3864",
  sectionFill: "FFD6E4F0",
  headerText: "FFFFFFFF",
  gray: "FF595959",
  border: "FFBFBFBF",
  link: "FF0563C1",
  overrideFill: "FFDDEBF7",
  redFill: "FFFFC7CE",
  redText: "FF9C0006",
  yellowFill: "FFFFEB9C",
  yellowText: "FF9C6500",
  greenFill: "FFC6EFCE",
  greenText: "FF006100",
};

/** Style token for a risk cell on the direct 0..2 scale (higher = riskier). */
export function riskStyleToken(risk) {
  if (risk === 2) return "riskHigh";
  if (risk === 1) return "riskMedium";
  if (risk === 0) return "riskNone";
  return "unscored";
}

/** Style token for a Method cell (how the privilege was received). */
export function methodStyleToken(method) {
  if (method === "Explicitly Denied") return "statusDenied";
  if (method === "Explicitly Granted") return "statusOverride";
  return null;
}

// Per-sheet presentation: title band, subtitle, column widths, and which
// columns blank out repeated values (blockKey defines the run: rows belong to
// the same block while its value repeats; alwaysBlankInBlock columns blank on
// every repeated row of the block, valueBlank columns blank only while their
// own value repeats consecutively).
const SHEET_META = {
  "All Users": {
    title: "All Users",
    subtitle:
      "Everyone and the privileges they hold — staff and patients, riskiest first (Risk 0–2, higher = riskier). Method shows how each privilege was received. Patients are identified by ID and username only; Unexpected Access marks privileges patients should not normally have. Repeated values are left blank for readability.",
    widths: [8, 16, 18, 20, 14, 10, 30, 38, 14, 20, 30, 16],
    blockKey: "User Unique ID",
    alwaysBlankInBlock: ["User Unique ID", "Username", "Display Name", "Account Type"],
    valueBlank: ["Method", "Source", "Unexpected Access"],
  },
  GroupPrivileges: {
    title: "GroupPrivileges",
    subtitle: "What each group grants, riskiest first. Repeated values are left blank for readability.",
    widths: [8, 10, 26, 12, 32, 40],
    blockKey: "Group ID",
    alwaysBlankInBlock: ["Group ID", "Group Name"],
    valueBlank: [],
  },
};

function styleTokenFor(header, value) {
  if (header === "Risk") {
    return riskStyleToken(value === "" ? null : value);
  }
  if (header === "Method") {
    return methodStyleToken(value);
  }
  if (header === "Unexpected Access") {
    if (value === "yes") return "flagBeyond";
    if (value === "no") return "centered";
    return null;
  }
  return null;
}

/** Blank repeated values, mutating nothing: returns new row objects. Within a
 *  block (same blockKey value as the previous row), alwaysBlankInBlock columns
 *  are blanked on every row after the first, and valueBlank columns are
 *  blanked while their own value repeats the previous row's. */
export function blankRepeats(rows, meta) {
  let prev = null;
  return rows.map((row) => {
    const out = { ...row };
    const sameBlock = prev !== null && prev[meta.blockKey] === row[meta.blockKey];
    if (sameBlock) {
      for (const col of meta.alwaysBlankInBlock || []) {
        out[col] = "";
      }
      for (const col of meta.valueBlank || []) {
        if (prev[col] === row[col]) {
          out[col] = "";
        }
      }
    }
    prev = row;
    return out;
  });
}

/**
 * The workbook's sheet order — the stakeholder view: the merged All Users tab,
 * then GroupPrivileges. Everything else lives only in the JSON mirror.
 */
export const WORKBOOK_SHEET_ORDER = ["All Users", "GroupPrivileges"];

/**
 * The full workbook spec: one styled data sheet per WORKBOOK_SHEET_ORDER
 * entry.
 * @param {object|null} reportData the resolver payload (#223)
 * @returns {{sheets: Array<object>}}
 */
export function buildWorkbookSpec(reportData) {
  const groupPrivileges = buildReportSheets(reportData).find((s) => s.name === "GroupPrivileges");
  const rowsByName = {
    "All Users": buildAllUsersRows(reportData),
    GroupPrivileges: groupPrivileges.rows,
  };

  const sheets = WORKBOOK_SHEET_ORDER.map((name) => {
    const meta = SHEET_META[name];
    const headers = SHEET_HEADERS[name];
    let subtitle = meta.subtitle;
    // Fail loud, never wrong-but-plausible: with no resolvable
    // patient baseline, every patient privilege flags — say so where the
    // reviewer is looking, instead of presenting an all-yes column as a real
    // finding.
    if (name === "All Users" && reportData?.patientPrivileges?.baselineResolved === false) {
      subtitle = `PATIENT BASELINE COULD NOT BE RESOLVED (no org Patients group found) — every patient privilege below is marked Unexpected. Fix the baseline before treating these as findings. ${subtitle}`;
    }
    const rows = blankRepeats(rowsByName[name], meta).map((row) =>
      headers.map((header) => {
        const cell = { value: row[header], style: styleTokenFor(header, row[header]) };
        if (cell.style === "unscored") cell.value = "unscored";
        return cell;
      })
    );
    return {
      name,
      title: meta.title,
      subtitle,
      widths: meta.widths,
      headers,
      rows,
    };
  });

  return { sheets };
}
